/**
 * Utilitários de cálculo sonoro conforme NBR 10151
 */

/**
 * Calcula o LAeq (Nível de Pressão Sonora Equivalente Contínuo)
 * @param {number[]} valores - Array de valores em dB
 * @returns {number} - LAeq calculado
 */
export const calcularLAeq = (valores) => {
  if (!valores || valores.length === 0) return 0;
  
  const valoresFiltrados = valores.filter(v => typeof v === 'number' && !isNaN(v) && v > 0);
  if (valoresFiltrados.length === 0) return 0;
  
  // Converte cada valor para intensidade linear
  const valoresTransformados = valoresFiltrados.map(v => Math.pow(10, v / 10));
  
  // Média das intensidades
  const soma = valoresTransformados.reduce((a, b) => a + b, 0);
  const media = soma / valoresTransformados.length;
  
  // Converte de volta para dB
  const resultado = 10 * Math.log10(media);
  
  return Math.round(resultado * 10) / 10;
};

/**
 * Calcula o valor máximo
 * @param {number[]} valores - Array de valores em dB
 * @returns {number} - Valor máximo
 */
export const calcularMaximo = (valores) => {
  if (!valores || valores.length === 0) return 0;
  const valoresFiltrados = valores.filter(v => typeof v === 'number' && !isNaN(v));
  return Math.max(...valoresFiltrados);
};

/**
 * Calcula o valor mínimo
 * @param {number[]} valores - Array de valores em dB
 * @returns {number} - Valor mínimo
 */
export const calcularMinimo = (valores) => {
  if (!valores || valores.length === 0) return 0;
  const valoresFiltrados = valores.filter(v => typeof v === 'number' && !isNaN(v) && v > 0);
  return Math.min(...valoresFiltrados);
};

/**
 * Detecta presença de som tonal conforme NBR 10151
 * @param {Array<{freq: number, db: number}>} bandas - Dados de frequência em bandas de 1/3 de oitava
 * @returns {{ presente: boolean, frequencias: number[] }}
 */
export const detectarSomTonal = (bandas) => {
  if (!bandas || bandas.length < 3) return { presente: false, frequencias: [] };
  
  const frequenciasTonais = [];
  
  for (let i = 1; i < bandas.length - 1; i++) {
    const freq = bandas[i].freq;
    const valorAtual = bandas[i].db;
    const valorAnterior = bandas[i - 1].db;
    const valorProximo = bandas[i + 1].db;
    
    // Define limiar conforme frequência (NBR 10151)
    let limiar;
    if (freq <= 125) {
      limiar = 15; // Frequências baixas
    } else if (freq >= 160 && freq <= 400) {
      limiar = 8; // Frequências médias-baixas
    } else {
      limiar = 5; // Frequências médias e altas
    }
    
    // Verifica se a banda atual é maior que as adjacentes pelo limiar
    if ((valorAtual - valorAnterior >= limiar) && (valorAtual - valorProximo >= limiar)) {
      frequenciasTonais.push(freq);
    }
  }
  
  return {
    presente: frequenciasTonais.length > 0,
    frequencias: frequenciasTonais
  };
};

/**
 * Detecta presença de som impulsivo
 * @param {number} laeq - Valor LAeq
 * @param {number} maximo - Valor máximo
 * @returns {boolean}
 */
export const detectarSomImpulsivo = (laeq, maximo) => {
  // Som impulsivo: diferença >= 6 dB entre máximo e LAeq
  return (maximo - laeq) >= 6;
};

/**
 * Calcula o nível específico (Lesp) - som da fonte investigada
 * @param {number} ltot - Nível total medido
 * @param {number} lres - Nível residual (sem a fonte)
 * @returns {{ valor: number | null, observacao: string }}
 */
export const calcularLesp = (ltot, lres) => {
  const diferenca = ltot - lres;
  
  if (diferenca > 15) {
    return {
      valor: ltot,
      observacao: 'Diferença > 15 dB: som específico predominante. Lesp = Ltot.'
    };
  }
  
  if (diferenca < 3) {
    return {
      valor: null,
      observacao: 'Diferença < 3 dB: não é possível determinar o Lesp com exatidão.'
    };
  }
  
  if (ltot > lres) {
    const lesp = 10 * Math.log10(Math.pow(10, ltot / 10) - Math.pow(10, lres / 10));
    return {
      valor: Math.round(lesp * 10) / 10,
      observacao: 'Lesp calculado conforme NBR 10151.'
    };
  }
  
  return {
    valor: null,
    observacao: 'Lres >= Ltot: cálculo inválido.'
  };
};

/**
 * Calcula o nível de resposta (Lresp) considerando correções
 * @param {number} lesp - Nível específico
 * @param {boolean} temTonal - Presença de som tonal
 * @param {boolean} temImpulsivo - Presença de som impulsivo
 * @returns {number | null}
 */
export const calcularLresp = (lesp, temTonal, temImpulsivo) => {
  if (lesp === null) return null;
  
  let lresp = lesp;
  
  // Adiciona 5 dB se houver som tonal
  if (temTonal) lresp += 5;
  
  // Adiciona 5 dB se houver som impulsivo
  if (temImpulsivo) lresp += 5;
  
  return Math.round(lresp * 10) / 10;
};

/**
 * Verifica conformidade com valor de referência
 * @param {number} lresp - Nível de resposta
 * @param {number} valorReferencia - Valor limite de referência
 * @returns {boolean}
 */
export const verificarConformidade = (lresp, valorReferencia) => {
  if (lresp === null) return false;
  return lresp < valorReferencia;
};

/**
 * Determina o método de avaliação
 * @param {boolean} temTonal - Presença de som tonal
 * @param {boolean} temImpulsivo - Presença de som impulsivo
 * @returns {'Simplificado' | 'Detalhado'}
 */
export const determinarMetodo = (temTonal, temImpulsivo) => {
  return (temTonal || temImpulsivo) ? 'Detalhado' : 'Simplificado';
};

/**
 * Processa um conjunto completo de medições
 * @param {Array} pontos - Array de pontos de medição
 * @returns {Array} - Resultados processados
 */
export const processarMedicoes = (pontos) => {
  if (!pontos || pontos.length < 2) {
    return { erro: 'Adicione pelo menos um ponto residual e um ponto específico.' };
  }
  
  // Encontra o ponto residual
  const pontoResidual = pontos.find(p => p.tipo === 'residual');
  if (!pontoResidual) {
    return { erro: 'É necessário um ponto residual como referência.' };
  }
  
  const resultadoResidual = {
    ...pontoResidual,
    laeq: calcularLAeq(pontoResidual.valores),
    maximo: calcularMaximo(pontoResidual.valores),
    minimo: calcularMinimo(pontoResidual.valores)
  };
  
  // Processa pontos específicos
  const resultados = pontos.filter(p => p.tipo === 'especifico').map(ponto => {
    const laeq = calcularLAeq(ponto.valores);
    const maximo = calcularMaximo(ponto.valores);
    const minimo = calcularMinimo(ponto.valores);
    
    const tonal = detectarSomTonal(ponto.frequencyData || []);
    const impulsivo = detectarSomImpulsivo(laeq, maximo);
    
    const lespCalc = calcularLesp(laeq, resultadoResidual.laeq);
    const metodo = determinarMetodo(tonal.presente, impulsivo);
    
    let lresp = null;
    if (metodo === 'Detalhado') {
      lresp = calcularLresp(lespCalc.valor, tonal.presente, impulsivo);
    } else {
      lresp = lespCalc.valor;
    }
    
    const atende = verificarConformidade(lresp, ponto.valorReferencia);
    
    return {
      ...ponto,
      laeq,
      maximo,
      minimo,
      tonal,
      impulsivo,
      lesp: lespCalc,
      lresp,
      metodo,
      atende,
      lresidual: resultadoResidual.laeq
    };
  });
  
  return {
    residual: resultadoResidual,
    especificos: resultados
  };
};

/**
 * Valores de referência NBR 10151
 */
export const VALORES_REFERENCIA = {
  diurno: {
    residencialRural: 40,
    residencialUrbana: 50,
    mistaPredominantementeResidencial: 55,
    mistaComercial: 60,
    mistaCulturalLazer: 65,
    industrial: 70
  },
  noturno: {
    residencialRural: 35,
    residencialUrbana: 45,
    mistaPredominantementeResidencial: 50,
    mistaComercial: 55,
    mistaCulturalLazer: 55,
    industrial: 60
  }
};

/**
 * Exemplos de níveis sonoros típicos
 */
export const EXEMPLOS_NIVEIS = [
  { nivel: 30, descricao: 'Biblioteca silenciosa' },
  { nivel: 40, descricao: 'Residência à noite' },
  { nivel: 50, descricao: 'Escritório tranquilo' },
  { nivel: 60, descricao: 'Conversa normal' },
  { nivel: 70, descricao: 'Tráfego urbano' },
  { nivel: 80, descricao: 'Rua movimentada' },
  { nivel: 90, descricao: 'Cortador de grama' },
  { nivel: 100, descricao: 'Serra elétrica' },
  { nivel: 110, descricao: 'Show de rock' },
  { nivel: 120, descricao: 'Avião decolando (próximo)' }
];
